<template>
  <div class="glass-card-demo">
    <h1>毛玻璃卡片组件示例</h1>
    
    <!-- 基础用法 -->
    <section class="demo-section">
      <h2>基础用法</h2>
      <div class="demo-grid">
        <GlassCard title="基础卡片">
          <p>这是一个基础的毛玻璃卡片，具有默认的样式和效果。</p>
          <p>支持任意内容，包括文本、图片、表单等。</p>
        </GlassCard>
        
        <GlassCard title="无悬停效果" :hoverable="false">
          <p>这个卡片禁用了悬停效果。</p>
          <p>适用于静态展示内容。</p>
        </GlassCard>
        
        <GlassCard title="无边框" :bordered="false">
          <p>这个卡片没有边框。</p>
          <p>看起来更加简洁。</p>
        </GlassCard>
      </div>
    </section>
    
    <!-- 不同变体 -->
    <section class="demo-section">
      <h2>不同变体</h2>
      <div class="demo-grid">
        <GlassCard title="Primary 变体" variant="primary">
          <p>默认的 primary 变体，适合大多数场景。</p>
        </GlassCard>
        
        <GlassCard title="Secondary 变体" variant="secondary">
          <p>Secondary 变体，带有淡蓝色调。</p>
        </GlassCard>
        
        <GlassCard title="Light 变体" variant="light">
          <p>Light 变体，更加透明明亮。</p>
        </GlassCard>
        
        <GlassCard title="Dark 变体" variant="dark">
          <p>Dark 变体，适合深色主题。</p>
        </GlassCard>
      </div>
    </section>
    
    <!-- 不同尺寸 -->
    <section class="demo-section">
      <h2>不同尺寸</h2>
      <div class="demo-grid">
        <GlassCard title="小尺寸" size="small">
          <p>小尺寸卡片，适合紧凑布局。</p>
        </GlassCard>
        
        <GlassCard title="中等尺寸" size="medium">
          <p>中等尺寸卡片，默认尺寸。</p>
        </GlassCard>
        
        <GlassCard title="大尺寸" size="large">
          <p>大尺寸卡片，适合重要内容展示。</p>
        </GlassCard>
      </div>
    </section>
    
    <!-- 自定义样式 -->
    <section class="demo-section">
      <h2>自定义样式</h2>
      <div class="demo-grid">
        <GlassCard 
          title="自定义透明度" 
          :opacity="0.3"
          width="300px"
        >
          <p>透明度设置为 0.3，更加不透明。</p>
        </GlassCard>
        
        <GlassCard 
          title="自定义模糊" 
          :blur="40"
          width="300px"
        >
          <p>模糊强度设置为 40px，更加模糊。</p>
        </GlassCard>
        
        <GlassCard 
          title="自定义圆角" 
          :border-radius="8"
          width="300px"
        >
          <p>圆角设置为 8px，更加方正。</p>
        </GlassCard>
      </div>
    </section>
    
    <!-- 插槽用法 -->
    <section class="demo-section">
      <h2>插槽用法</h2>
      <div class="demo-grid">
        <GlassCard>
          <template #header>
            <div class="custom-header">
              <span class="header-icon">🎨</span>
              <h3>自定义头部</h3>
              <button class="header-btn">操作</button>
            </div>
          </template>
          
          <p>这个卡片使用了自定义头部插槽。</p>
          <p>可以在头部放置任意内容。</p>
          
          <template #footer>
            <div class="custom-footer">
              <button class="footer-btn primary">确认</button>
              <button class="footer-btn">取消</button>
            </div>
          </template>
        </GlassCard>
        
        <GlassCard title="表单卡片">
          <form class="demo-form">
            <div class="form-group">
              <label>用户名</label>
              <input type="text" placeholder="请输入用户名" />
            </div>
            <div class="form-group">
              <label>邮箱</label>
              <input type="email" placeholder="请输入邮箱" />
            </div>
            <div class="form-group">
              <label>描述</label>
              <textarea placeholder="请输入描述"></textarea>
            </div>
          </form>
          
          <template #footer>
            <div class="form-footer">
              <button class="footer-btn primary">提交</button>
              <button class="footer-btn">重置</button>
            </div>
          </template>
        </GlassCard>
      </div>
    </section>
    
    <!-- 实际应用示例 -->
    <section class="demo-section">
      <h2>实际应用示例</h2>
      <div class="demo-grid">
        <GlassCard title="统计卡片" variant="secondary" size="small">
          <div class="stats-content">
            <div class="stats-number">1,234</div>
            <div class="stats-label">总用户数</div>
            <div class="stats-trend positive">+12.5%</div>
          </div>
        </GlassCard>
        
        <GlassCard title="通知中心" variant="light">
          <div class="notification-list">
            <div class="notification-item">
              <span class="notification-icon">📧</span>
              <div class="notification-content">
                <div class="notification-title">新消息</div>
                <div class="notification-desc">您有一条新的系统消息</div>
              </div>
              <div class="notification-time">2分钟前</div>
            </div>
            <div class="notification-item">
              <span class="notification-icon">⚠️</span>
              <div class="notification-content">
                <div class="notification-title">系统警告</div>
                <div class="notification-desc">磁盘空间不足</div>
              </div>
              <div class="notification-time">5分钟前</div>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard title="快速操作" :no-padding="true">
          <div class="quick-actions">
            <div class="action-item">
              <span class="action-icon">👥</span>
              <span class="action-text">用户管理</span>
            </div>
            <div class="action-item">
              <span class="action-icon">📊</span>
              <span class="action-text">数据分析</span>
            </div>
            <div class="action-item">
              <span class="action-icon">⚙️</span>
              <span class="action-text">系统设置</span>
            </div>
            <div class="action-item">
              <span class="action-icon">📝</span>
              <span class="action-text">日志查看</span>
            </div>
          </div>
        </GlassCard>
      </div>
    </section>
  </div>
</template>

<script setup>
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'

// 定义组件别名便于模板使用
const GlassCard = CanvasGlassCard
</script>

<style scoped>
.glass-card-demo {
  padding: 20px;
  min-height: 100vh;
}

.glass-card-demo h1 {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 40px;
  font-size: 32px;
  font-weight: 600;
}

.demo-section {
  margin-bottom: 50px;
}

.demo-section h2 {
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  border-bottom: 2px solid rgba(168, 200, 236, 0.3);
  padding-bottom: 10px;
}

.demo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

/* 自定义头部样式 */
.custom-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-icon {
  font-size: 20px;
  margin-right: 10px;
}

.custom-header h3 {
  margin: 0;
  flex: 1;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.header-btn {
  background: rgba(168, 200, 236, 0.2);
  border: 1px solid rgba(168, 200, 236, 0.3);
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
}

.header-btn:hover {
  background: rgba(168, 200, 236, 0.3);
  border-color: rgba(168, 200, 236, 0.5);
}

/* 自定义底部样式 */
.custom-footer,
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.footer-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid rgba(168, 200, 236, 0.3);
  background: rgba(255, 255, 255, 0.5);
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.footer-btn:hover {
  background: rgba(168, 200, 236, 0.2);
  border-color: rgba(168, 200, 236, 0.5);
}

.footer-btn.primary {
  background: linear-gradient(135deg, #a8c8ec, #c8b5db);
  color: white;
  border-color: transparent;
}

.footer-btn.primary:hover {
  background: linear-gradient(135deg, #96b8e0, #b8a5cb);
}

/* 表单样式 */
.demo-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea {
  padding: 10px 12px;
  border: 1px solid rgba(168, 200, 236, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #a8c8ec;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 3px rgba(168, 200, 236, 0.2);
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

/* 统计卡片样式 */
.stats-content {
  text-align: center;
}

.stats-number {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}

.stats-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 8px;
}

.stats-trend {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.stats-trend.positive {
  color: #27ae60;
  background: rgba(39, 174, 96, 0.1);
}

/* 通知列表样式 */
.notification-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.notification-item:hover {
  background: rgba(255, 255, 255, 0.5);
}

.notification-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.notification-desc {
  font-size: 12px;
  color: #7f8c8d;
}

.notification-time {
  font-size: 11px;
  color: #95a5a6;
  flex-shrink: 0;
}

/* 快速操作样式 */
.quick-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.action-item:nth-child(2n) {
  border-right: none;
}

.action-item:nth-last-child(-n+2) {
  border-bottom: none;
}

.action-item:hover {
  background: rgba(255, 255, 255, 0.2);
}

.action-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.action-text {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .demo-grid {
    grid-template-columns: 1fr;
  }
  
  .glass-card-demo {
    padding: 15px;
  }
  
  .glass-card-demo h1 {
    font-size: 24px;
  }
  
  .demo-section h2 {
    font-size: 20px;
  }
}
</style>